import * as _ from 'lodash';
import {ColumnsType} from 'cl-table/redux/actions';
import {SortPropsForQuery} from 'cl-table/consts';
import {productApi, UpdateProductBody} from 'cl-api';
import {flatten} from 'flat';
import {getColumnsSortProps, tableFilterToEsQuery, tableSelectionToEsQuery} from 'cl-table/utils/elasticsearchUtils';
import {StoreState} from '../../App/initialStore';
import createTableTaskProgress from 'cl-table/createTableTaskProgressBar';
import throwSubmissionError from 'cl-form/utils/throwSubmissionError';
import {ReduxTableOptions} from 'cl-table/redux/createTableContext';

export type AttributeRowData = {name: string; displayName: string};
export type ProductRowData = {_id: string};
export type InventoryRowData = {_id: string; _parentId?: string};

export async function getProductsRows(
  startIndex: number,
  stopIndex: number,
  sortProps: SortPropsForQuery,
  query: object,
  options?: object
): Promise<{rows: ProductRowData[]; count: number}> {
  const response = await (await productApi.getProducts(
    {
      from: startIndex,
      size: 1 + stopIndex - startIndex,
      sort: sortProps ? [sortProps] : null,
      query,
      options: options,
    },
    {credentials: 'include'}
  )).json();
  return {
    rows: response.hits.map(({_source, _id, fields: {thumbnail}}) => ({
      _id,
      thumbnail,
      ...flatten(_source, {safe: true}),
    })),
    count: response.total,
  };
}

export async function getProductRow(id: string, additionalData = {withListingsCount: true, withVariations: true}) {
  return (await getProductsRows(0, 0, null, tableSelectionToEsQuery(null, {include: [id]}), {
    additionalData,
  })).rows[0];
}

// TODO if you are reading this comment after 30/03/2020 ask semi if anyone needs the amazon properties and if not, delete the commented code below
// const hiddenFields = ['SalesRank', 'CurrencyCode'];
// const displayNameMapping = {
//   Amount: 'Price',
//   Height: 'Package Height',
//   Length: 'Package Length',
//   Weight: 'Package Weight',
//   'Width:': 'Package Width:',
// };

// const getAmazonProperties = (properties, rootName) => {
//   let mappableAmazonProperties = {};
//   _.forEach(properties, (column: any, columnName) => {
//     if (!_.includes(hiddenFields, columnName)) {
//       const AmazonPropertyName = `${rootName}.${columnName}`;
//       const displayName = `${displayNameMapping[columnName] ? displayNameMapping[columnName] : columnName} (Amazon)`;
//       if (_.isEmpty(column.properties)) {
//         mappableAmazonProperties[AmazonPropertyName] = {displayName, ...column};
//       } else {
//         mappableAmazonProperties = {
//           ...mappableAmazonProperties,
//           ...getAmazonProperties(column.properties, AmazonPropertyName),
//         };
//       }
//     }
//   });
//   return mappableAmazonProperties;
// };

export const getProductSchemaInfo: (() => Promise<{columns: ColumnsType; meta: any}>) & _.MemoizedFunction = _.memoize(
  () =>
    productApi.getProductSchemaInfo({
      credentials: 'include',
    })
);

export async function getProductSections(): Promise<string[]> {
  return (await getProductSchemaInfo()).meta.sections;
}

export const getProductsColumnsData: (() => Promise<ColumnsType>) & _.MemoizedFunction = _.memoize(
  async (): Promise<ColumnsType> => {
    const {columns} = await getProductSchemaInfo();
    const productProperties: ColumnsType = _.mapValues(
      _.omit(columns, ['AmazonProperties', 'eBayProperties']),
      (property, name) => ({displayName: name, ...property})
    );
    // TODO if you are reading this comment after 30/03/2020 ask semi if anyone needs the amazon properties and if not, delete the commented code below
    // const amazonProperties = getAmazonProperties(_.get(columns, 'AmazonProperties.properties'), 'AmazonProperties');
    return {...productProperties, /*...amazonProperties,*/ Imported: {hidden: true, filterReadonly: true}};
  }
);

export function clearCache() {
  getProductSchemaInfo.cache.clear();
  getProductsColumnsData.cache.clear();
}

export const createProduct = async (product): Promise<any> => {
  return _.first(
    (await productApi.createUpdateProducts({objects: [product], options: {updateExisting: false}, sync: true}))
      .newProducts
  );
};

export const updateProduct: (
  id: string,
  body: UpdateProductBody,
  options?: any
) => Promise<any> = productApi.updateProduct.bind(productApi);

export const createImportProgressBar = (tableName: string) => {
  return createTableTaskProgress<StoreState>(({general: {progress}}) => {
    if (!progress[tableName]) {
      return {inProgress: false, value: 0};
    }
    if (progress[tableName].done) {
      return {inProgress: false, value: 100};
    }
    return {inProgress: true, value: _.max([progress[tableName].percent, 10])};
  });
};

export const loadMoreProductRows = async ({startIndex, stopIndex}, state, noParents: boolean = false) => {
  let {columns, sortProps, filter} = state;
  const query = tableFilterToEsQuery(filter);
  return await getProductsRows(startIndex, stopIndex, getColumnsSortProps(sortProps, columns), query, {noParents});
};

export const getProductOnChange = (
  tableName: string,
  noParents: boolean = false
): ReduxTableOptions['onChange'] => async (selection, columnName, value, {filter}) => {
  try {
    if (_.size(selection.include) === 1) {
      await productApi.updateProduct(_.first(selection.include) as string, {
        product: {[columnName]: value},
        options: {refresh: [tableName]},
      });
    } else {
      await productApi.updateProducts(
        {
          query: tableSelectionToEsQuery(filter, selection),
          update: {[columnName]: value},
          options: {
            refresh: [tableName],
            noParents,
          },
        },
        {
          credentials: 'include',
        }
      );
    }
  } catch (e) {
    return throwSubmissionError(e);
  }
};
